import React, { useEffect, useState } from "react";
import ControlledInspectionTimeDropdown from "./ControlledInspectionTimeDropdown";
import useInspectionTimeSlotOptions from "../../../hooks/inspections/useInspectionTimeSlotOptions";
import { Control } from "react-hook-form";
import { Time, parseTime } from "@internationalized/date";

interface ControlledInspectionTimePickerProps {
  inspectionId?: string | number;
  name: string;
  control: Control;
}
export default function ControlledInspectionTimePicker({
  inspectionId,
  control,
  name,
}: ControlledInspectionTimePickerProps) {
  const [options, setOptions] = useState<{ value: string | null; label: string }[]>([]);

  const { data } = useInspectionTimeSlotOptions(inspectionId);

  useEffect(() => {
    if (data?.data) {
      setOptions([...data.data]);
    }
  }, [data?.data]);

  const handleCreate = (value: string) => {
    const timeAndMeridiem = value.trim().split(" ");

    if (timeAndMeridiem.length < 1 || timeAndMeridiem.length > 2) {
      control.formState.errors[name] = "Bad format";
      return;
    }

    const time = timeAndMeridiem[0].trim();

    let meridiem = "AM";
    if (timeAndMeridiem.length === 2) {
      meridiem = timeAndMeridiem[1].trim().toUpperCase();
    }
    if (meridiem !== "AM" && meridiem !== "PM") {
      control.formState.errors[name] = "Bad format";
      return;
    }

    const hoursAndMinutes = time.split(":");
    if (hoursAndMinutes.length < 2 || timeAndMeridiem.length > 2) {
      control.formState.errors[name] = "Bad format";
      return;
    }

    const hoursString = hoursAndMinutes[0];
    const minutesString = hoursAndMinutes[1];

    const hours = parseInt(hoursString);
    if (isNaN(hours)) {
      control.formState.errors[name] = "Bad format";
      return;
    }
    const minutes = parseInt(minutesString);
    if (isNaN(minutes)) {
      control.formState.errors[name] = "Bad format";
      return;
    }

    control.setValue(name, new Time((hours % 12) + (meridiem === "PM" ? 12 : 0), minutes, 0));
  };

  return (
    <ControlledInspectionTimeDropdown<Time | "AM" | "PM" | null>
      control={control}
      name={name}
      transform={{
        input: (value) => {
          if (value === null || value === "AM" || value === "PM") {
            return {
              value: value,
              label: value ?? "Anytime",
            };
          }
          return {
            value: value.toString(),
            label: `${value.hour % 12 === 0 ? 12 : value.hour % 12}:${
              value.minute > 9 ? value.minute : `0${value.minute}`
            } ${value.hour >= 12 ? "PM" : "AM"}`.trim(),
          };
        },
        output: (value) => {
          if (value === null) return null;
          if (value.value === "AM" || value.value === "PM" || value.value === null) {
            return value.value;
          }
          return parseTime(value.value);
        },
      }}
      inspectionTimeDropdownProps={{
        label: "Time",
        required: true,
        onCreate: handleCreate,
        options: options,
      }}
    />
  );
}
